import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Piramida from './lovac-kviz/Piramida';
import LovacKvizStartScreen from './lovac-kviz/LovacKvizStartScreen';
import { getKvizPitanje, getKvizLovacPitanjeRandom, markKvizLovacPitanjeOdgovoreno, saveKvizLovacRezultat } from '../../server/server-kviz';
import MDEditor from '@uiw/react-md-editor';
import { FaQuestion } from 'react-icons/fa';
import { playCorrectAnswerSound, playWrongAnswerSound } from '../utils/util';
import Pitanje from './lovac-kviz/Pitanje';
import { useUser } from '../../auth/UseUser';
import { kvizList } from './KvizHome';
import { formatVreme } from './lovac-kviz/KvizLovacRangLista';

export const TrajanjeDuzinePitanja = 18;

const tezinaMap: Record<number, number[]> = {
    1: [1, 1, 2, 2, 3], // selectedTezina = 1
    2: [1, 2, 2, 3, 4], // selectedTezina = 2
    3: [2, 3, 3, 4, 4], // selectedTezina = 3
};

function LovacKvizHome({ samoTakmicarski }: { samoTakmicarski?: boolean }) {
    const { setReturnedList, isSignedIn } = useUser();
    const navigate = useNavigate();
    const [mode, setMode] = useState<'start' | 'current' | 'answer' | 'pobeda'>('start');
    const [level, setLevel] = useState(0);
    const [pendingLevel, setPendingLevel] = useState<number | null>(null);
    const [lives, setLives] = useState(3);
    const [currentQuestion, setCurrentQuestion] = useState<any>(null);
    const [isAnswered, setIsAnswered] = useState(false);
    const [isGameOver, setIsGameOver] = useState(false);
    const [lastLostLifeIndex, setLastLostLifeIndex] = useState<number | null>(null);
    const [selectedTezina, setSelectedTezina] = useState<number>(2);
    const [loadingKvizPitanje, setLoadingKvizPitanje] = useState(false);
    const [pitanjeGreska, setPitanjeGreska] = useState<string | null>(null);
    const progressBar = useRef<any>(null);
    const timer = useRef<any>(null);

    // ---- Takmičarski mod (mereno vreme + rang lista) ----
    const [takmicarski, setTakmicarski] = useState(false);
    const [elapsedMs, setElapsedMs] = useState(0);
    const [finalVremeMs, setFinalVremeMs] = useState<number | null>(null);
    const [cuvanjeRezultata, setCuvanjeRezultata] = useState(false);
    const startTimeRef = useRef<number | null>(null);
    const stopwatchInterval = useRef<any>(null);
    const rezultatSacuvanRef = useRef(false);
    const currentQuestionRef = useRef<any>(null);
    const takmicarskiRef = useRef(false);
    const isAnsweredRef = useRef(false);
    const isActiveRef = useRef(true);
    const pobedaTimeout = useRef<any>(null);
    // tačan odgovor na poslednje pitanje u takmičarskom modu: vreme je stopirano, ide direktno na rezultat
    const [zavrsnoPitanje, setZavrsnoPitanje] = useState(false);

    useEffect(() => {
        currentQuestionRef.current = currentQuestion;
    }, [currentQuestion]);

    useEffect(() => {
        takmicarskiRef.current = takmicarski;
    }, [takmicarski]);

    useEffect(() => {
        isAnsweredRef.current = isAnswered;
    }, [isAnswered]);

    // prati da li je komponenta i dalje na ekranu (napuštanje stranice = unmount)
    useEffect(() => {
        isActiveRef.current = true;
        return () => {
            isActiveRef.current = false;
            if (pobedaTimeout.current) clearTimeout(pobedaTimeout.current);
            // ako je korisnik napustio takmičarski krug dok je pitanje bilo izvučeno a neodgovoreno,
            // pitanje se ipak obeležava kao iskorišćeno (bez zvuka i bez state promena)
            if (takmicarskiRef.current && currentQuestionRef.current?.id && !isAnsweredRef.current) {
                markKvizLovacPitanjeOdgovoreno(currentQuestionRef.current.id).catch(() => {});
            }
        };
    }, []);

    useEffect(() => {
        if (timer.current) {
            clearTimeout(timer.current);
        }

        const fetchData = async () => {
            if (mode === 'answer') {
                await loadPitanje(); // Čeka da se loadPitanje završi
                if (!isActiveRef.current) return; // korisnik je u međuvremenu napustio kviz
                timer.current = setTimeout(handleAnswer, TrajanjeDuzinePitanja * 1000);
            }
        };

        fetchData();
        return () => {
            if (timer.current) {
                clearTimeout(timer.current);
            }
        };
    }, [mode]);

    const handleStart = async () => {
        setMode('answer');
    };

    const handleStartTakmicarski = () => {
        if (!isSignedIn) {
            navigate('/login');
            return;
        }
        setTakmicarski(true);
        startTimeRef.current = Date.now();
        setElapsedMs(0);
        setFinalVremeMs(null);
        rezultatSacuvanRef.current = false;
        setMode('current');
    };

    const goToRangLista = () => {
        navigate('/user/kviz/lovac/rang-lista');
    };

    // živa štoperica dok traje takmičarski krug
    useEffect(() => {
        if (takmicarski && mode !== 'start' && mode !== 'pobeda' && startTimeRef.current) {
            stopwatchInterval.current = setInterval(() => {
                setElapsedMs(Date.now() - (startTimeRef.current as number));
            }, 250);
        }
        return () => {
            if (stopwatchInterval.current) clearInterval(stopwatchInterval.current);
        };
    }, [takmicarski, mode]);

    const loadPitanje = async () => {
        setLoadingKvizPitanje(true);
        setPitanjeGreska(null);

        const resp = takmicarski
            ? await getKvizLovacPitanjeRandom(level)
            : await getKvizPitanje({ tezina: (tezinaMap[selectedTezina]?.[level] ?? selectedTezina).toString() });

        if (!isActiveRef.current) return; // korisnik je u međuvremenu napustio kviz

        if (resp.status === 201) {
            setCurrentQuestion(resp.data);
            setTimeout(() => {
                if (progressBar.current) progressBar.current.classList.add('active');
            }, 100);
            setLoadingKvizPitanje(false);
        } else {
            console.log('error', resp.data);
            setPitanjeGreska(resp.data?.message || 'Trenutno nema dostupnih pitanja.');
            setLoadingKvizPitanje(false);
        }
    };

    // učitavanje iz localStorage
    useEffect(() => {
        const stored = localStorage.getItem('selectedTezina');
        if (stored) {
            setSelectedTezina(Number(stored));
        } else {
            setSelectedTezina(2); // default
        }
    }, []);

    // upis u localStorage kad god se promeni
    useEffect(() => {
        if (selectedTezina) {
            localStorage.setItem('selectedTezina', selectedTezina.toString());
        }
    }, [selectedTezina]);

    const handleAnswer = (odgovor: any) => {
        if (!isActiveRef.current) return; // korisnik je u međuvremenu napustio kviz
        if (isAnswered) return;
        if (timer.current) {
            clearTimeout(timer.current);
        }
        setIsAnswered(true);
        if (progressBar.current) progressBar.current.classList.remove('active');
        if (odgovor?.tacan) {
            playCorrectAnswerSound();
        } else {
            playWrongAnswerSound();
        }

        if (takmicarski && currentQuestionRef.current?.id) {
            markKvizLovacPitanjeOdgovoreno(currentQuestionRef.current.id).catch(() => {});
        }

        const tacan = odgovor?.tacan;
        if (tacan) {
            if (level < 4) {
                setPendingLevel(level + 1);
            } else if (level === 4) {
                // stigli smo do vrha - ako je takmičarski, zamrzavamo vreme odmah
                if (takmicarski && startTimeRef.current) {
                    if (stopwatchInterval.current) clearInterval(stopwatchInterval.current);
                    const zavrsnoVreme = Date.now() - startTimeRef.current;
                    setFinalVremeMs(zavrsnoVreme);
                    setElapsedMs(zavrsnoVreme);
                    setZavrsnoPitanje(true);
                    // kratko se vidi tačan odgovor, pa odmah rezultat (bez povratka na piramidu)
                    pobedaTimeout.current = setTimeout(() => {
                        if (!isActiveRef.current) return;
                        setMode('pobeda');
                        setIsAnswered(false);
                        setCurrentQuestion(null);
                        setZavrsnoPitanje(false);
                    }, 1000);
                }
                setPendingLevel(level + 1);
                //setIsGameOver(true);
            }
        } else {
            setPendingLevel(Math.max(0, level - 1));
            setLives((prev) => {
                const updatedLives = prev - 1;
                setLastLostLifeIndex(updatedLives);
                if (updatedLives === 0) {
                    setIsGameOver(true);
                }
                return updatedLives;
            });
        }
    };

    const onReturnToPiramida = () => {
        if (takmicarski && pendingLevel === 5) {
            setMode('pobeda');
        } else {
            setMode('current');
        }
        setIsAnswered(false);
        setCurrentQuestion(null);
    };

    const restartGame = () => {
        if (pobedaTimeout.current) clearTimeout(pobedaTimeout.current);
        setZavrsnoPitanje(false);
        setMode('start'); // vraćamo na početni ekran
        setLevel(0);
        setLives(3);
        setCurrentQuestion(null);
        setIsAnswered(false);
        setIsGameOver(false);
        setPendingLevel(null);
        setTakmicarski(false);
        setElapsedMs(0);
        setFinalVremeMs(null);
        startTimeRef.current = null;
        rezultatSacuvanRef.current = false;
        if (stopwatchInterval.current) clearInterval(stopwatchInterval.current);
    };

    // čuvanje rezultata na backend kada se stigne do "pobeda" ekrana
    useEffect(() => {
        const sacuvaj = async () => {
            if (mode === 'pobeda' && takmicarski && finalVremeMs !== null && !rezultatSacuvanRef.current) {
                rezultatSacuvanRef.current = true;
                setCuvanjeRezultata(true);
                await saveKvizLovacRezultat({ vremeMs: finalVremeMs, zivotaPreostalo: lives });
                setCuvanjeRezultata(false);
            }
        };
        sacuvaj();
    }, [mode, takmicarski, finalVremeMs, lives]);

    useEffect(() => {
        setReturnedList(kvizList);
    }, []);

    const renderLives = () => {
        const hearts = Array.from({ length: lives }, (_, i) => {
            const isLost = i >= lives;
            const isJustLost = i === lastLostLifeIndex;

            return (
                <span
                    key={i}
                    className={`text-2xl transition-all duration-300 ${
                        isLost ? (isJustLost ? 'text-grey-400 animate-shake' : 'text-grey-400') : 'text-red-500'
                    }`}
                >
                    ❤️
                </span>
            );
        });

        return (
            <div className="flex flex-col gap-1 items-center mb-2">
                <div className="flex gap-2 justify-center items-center">
                    <p className="text-white text-center text-lg font-semibold leading-6">Broj života:</p> {hearts}
                </div>
                {takmicarski && (
                    <p className="text-gold text-center text-lg font-bold leading-6">
                        Vreme: {formatVreme(finalVremeMs !== null ? finalVremeMs : elapsedMs)}
                    </p>
                )}
            </div>
        );
    };

    return (
        <div className="flex h-full flex-col items-center bg-blueDark ">
            <div className="flex w-full h-full flex-col items-center overflow-y-auto no-scroll pt-[20px] pb-[60px]">
                <div className="w-full max-w-[1100px] px-[10px]">
                    {mode !== 'start' && renderLives()}

                    {isGameOver ? (
                        <div className="w-full flex flex-col items-center justify-center">
                            <div className="bg-white p-6 rounded-lg shadow text-center max-w-[300px]">
                                <h2 className="text-2xl font-bold text-red-600 mb-4">Kraj igre</h2>
                                <p className="mb-6">
                                    Ispucao si sve živote.
                                    {takmicarski && ' Vreme se ne broji jer nisi stigao do vrha.'}
                                </p>
                                <button onClick={restartGame} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                                    Pokušaj ponovo
                                </button>
                            </div>
                        </div>
                    ) : mode === 'pobeda' ? (
                        <div className="w-full flex flex-col items-center justify-center">
                            <div className="bg-white p-6 rounded-lg shadow text-center max-w-[350px]">
                                <h2 className="text-2xl font-bold text-green-600 mb-4">Čestitamo, stigao si do vrha!</h2>
                                <p className="text-4xl font-bold text-blueDark mb-4">{finalVremeMs !== null ? formatVreme(finalVremeMs) : '--:--'}</p>
                                <p className="mb-6 text-sm text-grey-600">
                                    {cuvanjeRezultata ? 'Čuvanje rezultata...' : 'Rezultat je sačuvan u rang listi.'}
                                </p>
                                <div className="flex flex-col gap-3">
                                    <button onClick={goToRangLista} className="px-4 py-2 bg-gold text-blueDark font-bold rounded hover:opacity-90">
                                        Pogledaj rang listu
                                    </button>
                                    <button onClick={restartGame} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                                        Igraj ponovo
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : mode === 'start' ? (
                        <LovacKvizStartScreen
                            selectedTezina={selectedTezina}
                            setSelectedTezina={setSelectedTezina}
                            onStart={() => setMode('current')}
                            onStartTakmicarski={handleStartTakmicarski}
                            onRangLista={goToRangLista}
                            isSignedIn={isSignedIn}
                            samoTakmicarski={samoTakmicarski}
                        />
                    ) : mode === 'current' ? (
                        <Piramida
                            setLevel={setLevel}
                            pendingLevel={pendingLevel}
                            currentLevel={level}
                            onStart={handleStart}
                            nextLevelLabel={`Vrh`}
                            pokreniNovuIgru={restartGame}
                        />
                    ) : pitanjeGreska ? (
                        <div className="w-full flex flex-col items-center justify-center">
                            <div className="bg-white p-6 rounded-lg shadow text-center max-w-[300px]">
                                <p className="mb-6">{pitanjeGreska}</p>
                                <button onClick={restartGame} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                                    Nazad na početak
                                </button>
                            </div>
                        </div>
                    ) : (
                        !loadingKvizPitanje &&
                        currentQuestion && (
                            <Pitanje
                                currentQuestion={currentQuestion}
                                progressBar={progressBar}
                                handleAnswer={handleAnswer}
                                isAnswered={isAnswered}
                                onReturnToPiramida={onReturnToPiramida}
                                duration={TrajanjeDuzinePitanja}
                                takmicarski={takmicarski}
                                zavrsnoPitanje={zavrsnoPitanje}
                            />
                        )
                    )}

                    {loadingKvizPitanje && <div className="mt-10 text-white font-semibold">Učitavanje pitanja...</div>}
                </div>
            </div>
        </div>
    );
}

export default LovacKvizHome;
